import Link from "next/link";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LANGUAGES, changeLanguage } from "@/lib/i18n";

// Language switcher, reused in two places with two different trigger looks sharing one hover-
// dropdown shell (.rbt-user-wrapper.right-align-dropdown > .rbt-user-menu-list-wrapper, the
// same mechanism Offcanvas/User.js and Offcanvas/NotificationBell.js already use) and the same
// .rbt-lang-switcher pill buttons Settings' Appearance tab uses:
//  - variant="topbar" (default): a slim "EN v" text link matching the thin utility bar's other
//    links (phone number, social icons) -- lives in Header-Top/TopBar.js. This used to be a
//    round icon button sitting in the *main* nav's quick-access row instead, which crowded that
//    row enough to wrap the Log In/Join for Free CTAs at common laptop widths and just looked
//    out of place next to the notification bell/avatar -- moved here instead, which is also
//    where this exact template originally stubbed out an (unused, commented-out) language
//    switcher, so it's the template's own intended slot for this.
//  - variant="icon": the original round-button-in-quick-access look, kept in case a future page
//    needs it, but nothing currently renders it.
const LanguageMenu = ({ variant = "topbar" }) => {
  const { i18n, t } = useTranslation();
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const trigger =
    variant === "icon" ? (
      <Link className="rbt-round-btn" href="#" title={t("settings.language.title")} onClick={(e) => e.preventDefault()}>
        <i className="feather-globe"></i>
      </Link>
    ) : (
      <Link className="rbt-topbar-lang-trigger" href="#" title={t("settings.language.title")} onClick={(e) => e.preventDefault()}>
        <i className="feather-globe"></i>
        <span>{current.code.toUpperCase()}</span>
        <i className="feather-chevron-down"></i>
      </Link>
    );

  return (
    <li className={`access-icon rbt-user-wrapper right-align-dropdown ${variant === "icon" ? "d-none d-xl-block" : ""}`}>
      {trigger}

      <div className="rbt-user-menu-list-wrapper">
        <div className="inner">
          <div className="rbt-notification-dropdown__head">
            <span className="name">{t("settings.language.title")}</span>
          </div>

          <div className="rbt-lang-switcher rbt-lang-switcher--header" role="radiogroup" aria-label={t("settings.language.title")}>
            {SUPPORTED_LANGUAGES.map((lang) => {
              const isActive = lang.code === current.code;
              return (
                <button
                  type="button"
                  key={lang.code}
                  role="radio"
                  aria-checked={isActive}
                  className={`rbt-lang-switcher__option ${isActive ? "is-active" : ""}`}
                  onClick={() => changeLanguage(lang.code)}
                >
                  {lang.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </li>
  );
};

export default LanguageMenu;
